import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SellIcon from '@mui/icons-material/Sell';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatNumber } from '../../utils/formatUtils';
import { PageHeader, useNotify } from '../../components/common';

const limitText = (v) => (v == null ? '무제한' : `${v}개`);

const PlanEditDialog = ({ plan, onClose, onSaved }) => {
  const notify = useNotify();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (plan) {
      setError('');
      setForm({
        name: plan.name,
        price: String(plan.price),
        description: plan.description || '',
        maxWatchlist: plan.maxWatchlist == null ? '' : String(plan.maxWatchlist),
        maxAutoTrading: plan.maxAutoTrading == null ? '' : String(plan.maxAutoTrading),
        isActive: plan.isActive,
        sortOrder: String(plan.sortOrder),
      });
    }
  }, [plan]);

  if (!plan || !form) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    try {
      await apiClient.put(`/admin/plans/${plan.id}`, {
        ...form,
        maxWatchlist: form.maxWatchlist === '' ? null : form.maxWatchlist,
        maxAutoTrading: form.maxAutoTrading === '' ? null : form.maxAutoTrading,
      });
      notify('요금제를 수정했습니다.');
      onSaved();
      onClose();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <SellIcon color="primary" />
        요금제 수정 — {plan.code}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="이름" value={form.name} onChange={set('name')} />
          <TextField label="가격 (원)" type="number" value={form.price} onChange={set('price')} />
          <TextField label="설명" value={form.description} onChange={set('description')} />
          <TextField label="관심종목 한도 (비우면 무제한)" type="number" value={form.maxWatchlist} onChange={set('maxWatchlist')} />
          <TextField label="자동매매 종목 한도 (비우면 무제한)" type="number" value={form.maxAutoTrading} onChange={set('maxAutoTrading')} />
          <TextField label="정렬 순서" type="number" value={form.sortOrder} onChange={set('sortOrder')} />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />}
            label="판매 중 (끄면 신규 부여 목록에서 숨김)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={save}>저장</Button>
      </DialogActions>
    </Dialog>
  );
};

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/plans');
      setPlans(res.data.plans);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <PageHeader title="요금제" description="한도 값은 에이전트가 /api/agent/owner 로 받아 관심종목·자동매매 개수 제한에 씁니다." />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>코드</TableCell>
              <TableCell>이름</TableCell>
              <TableCell>주기</TableCell>
              <TableCell align="right">가격</TableCell>
              <TableCell>관심종목</TableCell>
              <TableCell>자동매매</TableCell>
              <TableCell>설명</TableCell>
              <TableCell>상태</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{p.code}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.billingCycleLabel}</TableCell>
                <TableCell align="right">{formatNumber(p.price)}원</TableCell>
                <TableCell>{limitText(p.maxWatchlist)}</TableCell>
                <TableCell>{limitText(p.maxAutoTrading)}</TableCell>
                <TableCell>{p.description || '-'}</TableCell>
                <TableCell>
                  <Chip size="small" label={p.isActive ? '판매중' : '중지'} color={p.isActive ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setEditing(p)}>수정</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <PlanEditDialog plan={editing} onClose={() => setEditing(null)} onSaved={load} />
    </>
  );
};

export default Plans;
